import { InteractionEvent, SignInIdentifier } from '@logto/schemas';

import api from '../api';

import { experienceApiRoutes, type VerificationResponse } from './const';

export type PhoneSignInConsent = {
  accepted: true;
  termsVersion: string;
  privacyVersion: string;
};

const checkAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('Authentication cancelled', 'AbortError');
  }
};

/** Credentials and proof stay in the identity origin, on the native Experience chain. */
export async function sendContextualPhoneCode(
  phone: string,
  consent: PhoneSignInConsent,
  initialization: string | undefined | { preserveInteraction: true },
  signal: AbortSignal
): Promise<VerificationResponse> {
  if (typeof initialization !== 'object') {
    await api.put(experienceApiRoutes.prefix, {
      json: { interactionEvent: InteractionEvent.SignIn, captchaToken: initialization },
      signal,
    });
  }
  checkAborted(signal);
  return api
    .post(`${experienceApiRoutes.verification}/verification-code`, {
      json: {
        interactionEvent: InteractionEvent.SignIn,
        identifier: { type: SignInIdentifier.Phone, value: phone },
        purpose: 'phone_sign_in_or_register',
        consent,
      },
      signal,
    })
    .json<VerificationResponse>();
}

export async function submitContextualPhoneCode(
  phone: string,
  code: string,
  verificationId: string,
  signal: AbortSignal
): Promise<{ redirectTo: string }> {
  const verified = await api
    .post(`${experienceApiRoutes.verification}/verification-code/verify`, {
      json: { identifier: { type: SignInIdentifier.Phone, value: phone }, code, verificationId },
      signal,
    })
    .json<VerificationResponse>();
  checkAborted(signal);
  // Native identification owns both branches. No browser existence lookup or
  // user-not-found fallback is allowed to create an account here.
  await api.post(experienceApiRoutes.identification, {
    json: { verificationId: verified.verificationId },
    signal,
  });
  checkAborted(signal);
  return api.post(experienceApiRoutes.submit, { signal }).json<{ redirectTo: string }>();
}
